import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { filter, map, switchMap } from 'rxjs';

import { AttachmentModel, FileItemModel } from '@models/module/fields/main';
import { SystemModulesEnum } from '@models/module/main/systemModules.enum';
import {
  NoteDialogComponent,
  NoteDialogData,
  NoteDialogFile,
  NoteDialogResult,
} from '@module-definition/file-explorer/common/note-dialog/note-dialog.component';
import { AddNewComponent } from '@module-instance/attachments-dialog/add-new/add-new.component';
import {
  DriveAddNewModel,
  DriveAddNewOutput,
  DriveTableActionEvent,
  DriveTableActionsEnum,
  DriveTableOptions,
  DriveTableRow,
  MyDriveService,
} from '@module-instance/my-drive/common';
import { FiltersToApply } from '@shared/components/data-list/common';
import { DataListModule } from '@shared/components/data-list/data-list.module';
import { AppColors, FILE_SIZE_CONVERT, PAGE_SIZE_OPTIONS } from '@shared/consts';
import { CipoDialogHeaderComponent } from '@shared/dialogs';
import { CipoFormModule } from '@shared/modules/forms.module';
import { ImageSourcePipe } from '@shared/pipes/string-pipes/image-source.pipe';
import { NotificationService, UserService } from '@shared/services';
import { UserStore } from '@signal-store/user.store';

const ROOT_FOLDER_ID = -1;

export type AttachmentsDialogData = {
  multiple?: boolean;
  attachments: AttachmentModel[];
};

@Component({
  selector: 'app-attachments-dialog',
  imports: [
    MatDialogModule,
    CipoFormModule,
    TranslateModule,
    DataListModule,
    ImageSourcePipe,
    CipoDialogHeaderComponent,
  ],
  templateUrl: './attachments-dialog.component.html',
})
export class AttachmentsDialogComponent implements OnInit {
  rootFolderId = ROOT_FOLDER_ID;
  data = inject<AttachmentsDialogData>(MAT_DIALOG_DATA);
  dialog = inject(MatDialog);
  readonly dialogRef = inject(MatDialogRef<AttachmentsDialogComponent>);
  driveService = inject(MyDriveService);
  notificationService = inject(NotificationService);
  userService = inject(UserService);
  userStore = inject(UserStore);

  driveModuleName = computed(() => {
    return (
      this.userStore.menuItemsList()?.find(item => item.moduleId === SystemModulesEnum.MyCIPODrive)?.name || 'My drive'
    );
  });

  attachments = signal(this.data.attachments || []);
  canSave = signal(false);
  currentFolderId = signal(ROOT_FOLDER_ID);
  parentFolderId = signal<number>(null);
  searchCriteria = signal('');

  isLoading = signal(true);
  tableColumns = signal(this.driveService.getTableColumns());
  tableData = signal<DriveTableRow[]>(undefined);
  updatedRows = signal<DriveTableRow[]>([]);
  tableOptions = signal<DriveTableOptions>({
    clickableRows: true,
    pagination: {
      pageSize: PAGE_SIZE_OPTIONS.default[1],
      pageSizeOptions: PAGE_SIZE_OPTIONS.default,
    },

    actions: () => this.driveService.getTableActions(),
  });

  ngOnInit() {
    this.openFolder(ROOT_FOLDER_ID);
  }

  onSave() {
    this.dialogRef.close(this.attachments());
  }

  openFolder(id: number) {
    this.isLoading.set(true);
    this.tableData.set(null);
    const selectedFiles = this.attachments().map(file => file.id);
    this.parentFolderId.set(this.currentFolderId());
    const criteria = this.searchCriteria();
    const searchParams = { id, ...(criteria ? { criteria } : {}) };
    this.driveService.searchFiles(searchParams).subscribe(({ data, records }) => {
      this.tableData.set(
        data.map(
          file =>
            ({
              ...file,
              image: { type: file.isFolder ? 'icon' : 'image', iconKey: 'folder', class: 'cipo-text--primary-900' },
              fileSizeDisplay: file.isFolder ? null : this.formatFileSize(file.length),
              _originalLength: file.length,
              backgroundColor: selectedFiles.includes(file.id) ? AppColors.accent_50 : undefined,
            }) as DriveTableRow,
        ),
      );
      this.currentFolderId.set(id);
      this.isLoading.set(false);
      this.tableOptions.update(options => ({ ...options, pagination: { ...options.pagination, length: records } }));
    });
  }

  private formatFileSize(bytes: number): string {
    if (!bytes) {
      return null;
    }
    const unit = bytes < 100 * FILE_SIZE_CONVERT.KB ? 'KB' : 'MB';
    const divisor = unit === 'KB' ? FILE_SIZE_CONVERT.KB : FILE_SIZE_CONVERT.MB;
    return `${Number((bytes / divisor).toFixed(2))}${unit}`;
  }

  addNewFolder() {
    this.dialog
      .open<AddNewComponent, DriveAddNewModel, DriveAddNewOutput>(AddNewComponent, {
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        data: { modalType: 'newFolder', parentId: this.currentFolderId() },
        ...this.userService.getResponsiveDialogSize().sm,
      })
      .afterClosed()
      .pipe(
        filter(output => !!output?.name),
        switchMap(({ name }) => this.driveService.createFolder({ name, parentId: this.currentFolderId() })),
      )
      .subscribe(() => {
        this.notificationService.success('fileExplorer.folderCreated');
        this.openFolder(this.currentFolderId());
      });
  }

  uploadFile() {
    this.dialog
      .open<AddNewComponent, DriveAddNewModel, DriveAddNewOutput>(AddNewComponent, {
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        data: { modalType: 'newFile', parentId: this.currentFolderId() },
        ...this.userService.getResponsiveDialogSize().lg,
      })
      .afterClosed()
      .pipe(filter(output => !!output?.files))
      .subscribe(({ files }) => {
        this.attachments.update(items => [...items, ...files]);
        this.openFolder(this.currentFolderId());
        this.canSave.set(true);
      });
  }

  goBack() {
    const parents = this.tableData()[0]?.parents;
    const parent = parents?.length >= 2 ? parents[parents.length - 2] : this.parentFolderId();
    const parentId = typeof parent === 'number' ? parent : (parent?.id ?? ROOT_FOLDER_ID);
    this.openFolder(parentId);
  }

  rename(file: FileItemModel) {
    const fileExtension = file.isFolder ? '' : file.name.substring(file.name.lastIndexOf('.'));
    this.dialog
      .open<AddNewComponent, DriveAddNewModel, DriveAddNewOutput>(AddNewComponent, {
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        data: {
          modalType: 'rename',
          parentId: file.parentId,
          name: file.isFolder ? file.name : file.name.substring(0, file.name.lastIndexOf('.')),
          mimeType: fileExtension,
        },
        ...this.userService.getResponsiveDialogSize().sm,
      })
      .afterClosed()
      .pipe(
        filter(output => !!output?.name),
        switchMap(({ name }) => {
          const params = { ...file, name: name + fileExtension };
          return file.isFolder ? this.driveService.editFolder(params) : this.driveService.renameFile(params);
        }),
      )
      .subscribe(() => {
        this.notificationService.success('fileExplorer.' + (file.isFolder ? 'folderRenamed' : 'fileRenamed'));
        this.openFolder(this.currentFolderId());
      });
  }

  selectRow(file: DriveTableRow) {
    if (file.isFolder) {
      this.openFolder(file.id);
    } else {
      /* Enable save button */
      this.canSave.set(true);

      if (!this.data.multiple) {
        /* remove highlight from previously selected row */
        this.toggleRow(this.attachments()[0]?.id, false);
        /* highlight the newly selected row */
        this.toggleRow(file.id, true);
        /* Update the attachments */
        const originalLength = (file as DriveTableRow & { _originalLength?: number })._originalLength;
        const newFile = { ...file, length: originalLength ?? file.length } as AttachmentModel;
        this.attachments.set([newFile]);
      } else {
        const fileIndex = this.attachments().findIndex(f => f.id === file.id);
        if (fileIndex === -1) {
          /* Add the newly selected file */
          const originalLength = (file as DriveTableRow & { _originalLength?: number })._originalLength;
          const newFile = { ...file, length: originalLength ?? file.length } as AttachmentModel;
          this.attachments.update(files => [...files, newFile]);
          this.updatedRows.set([{ ...file, backgroundColor: AppColors.accent_50 }]);
        } else {
          /* Remove the unselected file */
          this.attachments.update(files => files.filter(f => f.id !== file.id));
          this.updatedRows.set([{ ...file, backgroundColor: undefined }]);
        }
      }
    }
  }

  chipClick(item: AttachmentModel) {
    if (!item.parentId || item.parentId === this.currentFolderId()) {
      return;
    }
    this.openFolder(item.parentId);
  }

  removeChip(item: AttachmentModel) {
    this.attachments.update(files => files.filter(f => f.id !== item.id));
    const row = this.tableData().find(r => r.id === item.id);
    if (row) {
      this.updatedRows.set([{ ...row, backgroundColor: undefined }]);
    }
  }

  searchFiles({ criteria }: FiltersToApply) {
    this.searchCriteria.set(criteria);
    this.openFolder(this.currentFolderId());
  }

  onAction(event: DriveTableActionEvent) {
    switch (event.actionId) {
      case DriveTableActionsEnum.VIEW:
        this.openFolder(event.data.id);
        break;
      case DriveTableActionsEnum.RENAME:
        this.rename(event.data);
        break;
      case DriveTableActionsEnum.NOTE:
        this.editRowNote(event.data);
        break;
      case DriveTableActionsEnum.OPEN_TAB:
        this.driveService
          .getFileUrl(event.data.id, event.data.versionId, true)
          .subscribe(url => window.open(url, '_blank', 'noopener,noreferrer'));
    }
  }

  private editRowNote(file: DriveTableRow) {
    this.openNoteDialog(file, file.note ?? '')
      .pipe(
        switchMap(savedNote =>
          this.driveService
            .saveFile({ id: file.id, versionId: file.versionId, note: savedNote })
            .pipe(map(() => savedNote)),
        ),
      )
      .subscribe(savedNote => {
        this.updatedRows.set([{ ...file, note: savedNote }]);
        this.attachments.update(items => items.map(a => (a.id === file.id ? { ...a, note: savedNote } : a)));
        this.notificationService.success('fileExplorer.noteSaved');
      });
  }

  editChipNote(item: AttachmentModel) {
    this.openNoteDialog(item, item.note ?? '').subscribe(savedNote => {
      this.attachments.update(items => items.map(a => (a.id === item.id ? { ...a, note: savedNote } : a)));
      this.canSave.set(true);
    });
  }

  private openNoteDialog(file: NoteDialogFile, note: string) {
    return this.dialog
      .open<NoteDialogComponent, NoteDialogData, NoteDialogResult>(NoteDialogComponent, {
        panelClass: ['cipo-dialog', 'classic'],
        data: { file, note },
        ...this.userService.getResponsiveDialogSize().md,
      })
      .afterClosed()
      .pipe(
        filter((result): result is NoteDialogResult => !!result),
        map(result => result.note),
      );
  }

  private toggleRow(fileId: number, selected: boolean) {
    if (fileId === undefined) {
      return;
    }
    this.tableData.update(rows => {
      const row = rows.find(r => r.id === fileId);
      if (row) {
        row.backgroundColor = selected ? AppColors.accent_50 : undefined;
      }
      return rows;
    });
  }
}
